import React, { useCallback, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useQuery, gql } from '@apollo/client';
import Lottie from 'react-lottie-player';

import { ReactComponent as LightImg } from '../../assets/ExpenseLight.svg';
import { ReactComponent as DarkImg } from '../../assets/ExpenseDark.svg';
import Table from 'Components/Table';
import formatDate from 'Shared/util/formatDate';
import EmptyPage from 'Components/EmptyPage';
import animationData from 'Shared/spiner_blue.json';
import withErrorBoundary from 'Components/ErrorBoundary/ErrorBoundary';
import { logger } from 'RootShared/utility/ErrorLogger';
import useDocumentTitle from 'Shared/hooks/useDocumentTitle';

export const queryGetExpenses = gql`
  query TabHistoryExpensesQuery @api(name: root) {
    expenses {
      amount
      reason {
        text
        id
      }
      date
      status
    }
  }
`;

const columns = [
  {
    title: 'AMOUNT',
    cellProps: {
      accessor: 'amount',
      minWidth: 210,
    },
  },
  {
    title: 'REASON',
    cellProps: {
      accessor: 'reason',
      minWidth: 412,
    },
  },
  {
    title: 'DATE',
    cellProps: {
      accessor: 'date',
      minWidth: 264,
    },
  },
  {
    title: 'STATUS',
    cellProps: {
      accessor: 'status',
      minWidth: 180,
    },
  },
];

const TabHistory = () => {
  const { loading, data, error, refetch } = useQuery(queryGetExpenses);

  useDocumentTitle('Expenses - History');

  useEffect(() => {
    refetch();
  }, [refetch]);

  const getTabHistoryTableComponent = useCallback((data = []) => {
    const { expenses = [] } = data;

    if (expenses.length) {
      return (
        <div className="tab-expenses-history" data-testid="tab_expenses_history">
          <div className="container">
            <Table
              sortable={false}
              {...{
                data: expenses.map((item) => ({
                  ...item,
                  amount: {
                    title: `${item.amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} UAH`,
                  },
                  reason: {
                    title: `${item.reason.text}`,
                  },
                  date: {
                    title: formatDate(item.date),
                  },
                  status: {
                    tagProps: {
                      text: item.status,
                      variant: item.status === 'Requested' ? 'yellow' : item.status === 'Rejected' ? 'red' : 'green',
                    },
                  },
                })),
                columns,
                tableSettings: {
                  headerHeight: 90,
                  paginationHeight: 56,
                  rowHeight: 70,
                  tablePadding: 32,
                  pageSize: window.innerWidth > 767 ? 10 : 3,
                },
              }}
            />
          </div>
        </div>
      );
    }

    return (
      <EmptyPage
        img={JSON.parse(localStorage.getItem('nightMode')) ? <DarkImg /> : <LightImg />}
        title={'Blank expenses history'}
        text={"Looks like you haven't had a expenses yet.To request your first expenses go to tab Request. "}
      />
    );
  }, []);

  if (error) {
    logger.error(error);
    toast(error.message, {
      type: toast.TYPE.ERROR,
      autoClose: 3000,
      closeButton: true,
      hideProgressBar: false,
    });
  }

  return (
    <>
      {loading && (
        <Lottie
          data-testid="tab_expenses_history"
          className="lottie"
          loop={true}
          play={true}
          animationData={animationData}
          width={100}
          style={{ width: 100, height: 100, margin: '0px auto' }}
        />
      )}
      {data && getTabHistoryTableComponent(data)}
    </>
  );
};

export default withErrorBoundary(TabHistory);
